import React, { useState } from 'react';

import { BackrsChip } from '@backrs_ui/common-ui';

import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';

import { Box, Theme, SxProps } from '@mui/material';

import { boxTitles } from './SessionStrings';
import { SolutionBox } from './SolutionBox';
import { UserInfoBoxes } from './UserInfoBoxes';
type InfoBoxProps = Readonly<{
  prefsRef1?: React.RefObject<HTMLDivElement>;
  prefsRef2?: React.RefObject<HTMLDivElement>;
}>;

export const InfoBox = ({ prefsRef1, prefsRef2 }: InfoBoxProps) => {
  const activeSession = useAppSelector(getActiveLubavSession);

  const { boxData } = useAppSelector((state) => getSessionState(state, activeSession).boxData);

  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    return null;
  }

  const numUserInfoBoxes = (boxTitles[activeSession] || []).length;

  // function for rendering the title chips since it may be rendered here
  // or in the solution box
  const renderTitleChips = () =>
    (boxTitles[activeSession] || []).map((title, index) => {
      const dataKey = `box_${index + 1}`;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const collectedData = (boxData.collectedData as any)?.[dataKey];

      return (
        collectedData.value?.some((val: string) => val !== '') && (
          <Box key={dataKey} sx={titleSectionStyles}>
            <Box sx={{ width: 'fit-content' }}>
              <BackrsChip
                label={title}
                variant={{
                  type: 'LARGE_TEXT',
                  backgroundColor: isCollapsed ? 'LIGHT_BLUE' : 'BLUE',
                }}
                onClick={() => setIsCollapsed((prev) => !prev)}
              />
            </Box>
          </Box>
        )
      );
    });

  return (
    <Box sx={containerStyles}>
      {numUserInfoBoxes > 0 && (
        <>
          {(!isCollapsed || !boxData.mainResponse) && (
            <Box sx={titleContainerStyles}>{renderTitleChips()}</Box>
          )}
          {!isCollapsed && (
            <Box sx={innerContainerStyles}>
              {(boxTitles[activeSession] || []).map((title, index) => {
                const dataKey = `box_${index + 1}`;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const collectedData = (boxData.collectedData as any)?.[dataKey];

                return (
                  collectedData?.value?.some((val: string) => val !== '') && (
                    <UserInfoBoxes
                      key={dataKey}
                      title={title}
                      dataKey={dataKey}
                      ref={index === 0 ? prefsRef1 : prefsRef2}
                    />
                  )
                );
              })}
            </Box>
          )}
        </>
      )}
      <SolutionBox collapsedChips={isCollapsed ? renderTitleChips() : null} />
    </Box>
  );
};

const containerStyles: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 'calc(100% - 30px)',
  maxHeight: 'calc(100vh - 76px)',
  gap: '10px',
  borderRadius: '8px',
  marginLeft: '30px',
  '@media (max-width: 1200px)': {
    marginBottom: '30px',
    paddingBottom: '30px',
    marginLeft: '30px',
    width: '100%',
  },
  '@media (max-width: 900px)': {
    marginLeft: '0px',
  },
};

const innerContainerStyles: SxProps<Theme> = {
  display: 'flex',
  height: 'auto',
  gap: '30px',
  width: '100%',
  '@media (max-width: 1200px)': {
    flexWrap: 'wrap',
  },
};

const titleContainerStyles: SxProps<Theme> = {
  display: 'flex',
  gap: '30px',
  width: '100%',
};

const titleSectionStyles: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
};
